import { castError } from '@sketch/utils'
import React, { useEffect, useState } from 'react'
import { UsageMetricsLoadStatus, UsageMetricsStatus } from './types'
import { GraphQLSchema, buildClientSchema } from 'graphql'
import { CostsRaw } from '../report/report.types'
import { readSettings } from '../../settings/settings.localStorage'
import { hookUsageMetrics } from '@sketch/graphql-apollo/useQuery'

const usageMetricsRef: { current: UsageMetricsStatus } = {
  current: { status: 'non-init' },
}

export const getUsageMetrics = (): UsageMetricsStatus => usageMetricsRef.current

const getVendorsBundleName = async (): Promise<string> => {
  /**
   * NOTE, there is a common confusion between `process.env.NODE_ENV` and `process.env.REACT_APP_ENV`
   *  - `process.env.NODE_ENV === 'production'` means that the app was built using `pnpm run build` instead of
   *     the `pnpm run start` command
   *  - `process.env.REACT_APP_ENV === 'production'` means that the app was built targeting production APIs
   *     or roughly speaking targeting www.sketch.com
   *
   * specifically here when we start the app using `pnpm run start` command, we always get the same bundle names,
   * and vendors name is always `'vendors~main.chunk.js'` so there is no need to load anything. However, when
   * we use `pnpm run build` now all bundles get their hashed filenames for which we need to get vendors-name.txt
   * */
  if (process.env.NODE_ENV === 'production') {
    const response = await fetch('/static/vendors-name.txt', {
      method: 'get',
      headers: { 'Content-Type': 'text/plain' },
    })

    if (response.status < 300) {
      const responseText = await response.text()
      const trimmedResponse = responseText ? responseText.trim() : ''
      return trimmedResponse
    }
    throw new Error('could not load vendors bundle name')
  } else {
    return 'vendors~main.chunk.js'
  }
}

const getGraphQLSchema = async (): Promise<GraphQLSchema> => {
  const response = await fetch('/static/usage-metrics.schema.json', {
    method: 'get',
    headers: { 'Content-Type': 'application/json' },
  })

  if (response.status < 300) {
    const introspectionQuery = (await response.json()).data
    const schema = buildClientSchema(introspectionQuery)
    return schema
  }
  throw new Error('could not load GraphQL Schema')
}

const getCostsJson = async (): Promise<CostsRaw> => {
  const response = await fetch('/static/usage-metrics.costs.json', {
    method: 'get',
    headers: { 'Content-Type': 'application/json' },
  })

  if (response.status < 300) {
    const costsRaw = (await response.json()) as CostsRaw
    return costsRaw
  }

  throw new Error('could not load Backend Costs JSON')
}

const loadUsageMetrics = async (): Promise<UsageMetricsLoadStatus> => {
  // exclude this entire code block from the build bundle targeting staging or production
  if (
    process.env.REACT_APP_ENV === 'dev' ||
    process.env.REACT_APP_ENV === 'test'
  ) {
    // if usage metrics were already initialized, return it
    if (usageMetricsRef.current.status !== 'non-init')
      return usageMetricsRef.current

    try {
      const storedDevToolsState = readSettings()

      const isEnabled =
        storedDevToolsState.usageMetrics?.isTurnedOn &&
        storedDevToolsState.general?.isTurnedOn

      if (!isEnabled) {
        usageMetricsRef.current = { status: 'not-enabled' }
        return usageMetricsRef.current
      }

      const [
        usageMetricsModule,
        vendorsBundleName,
        graphqlSchema,
        costsRaw,
      ] = await Promise.all([
        import('../usageMetrics'),
        getVendorsBundleName(),
        getGraphQLSchema(),
        getCostsJson(),
      ])

      const usageMetrics = usageMetricsModule.createUsageMetrics({
        vendorsBundleName,
        graphqlSchema,
        costsRaw,
      })

      usageMetricsRef.current = {
        status: 'loaded',
        usageMetrics,
      }

      hookUsageMetrics((result, query) => {
        result.data = usageMetrics.proxyData(result.data, query)
      })

      // @ts-expect-error add global variable for debugging
      window.__usageMetrics = usageMetrics
    } catch (e) {
      const err = castError(e)
      usageMetricsRef.current = { status: 'error', reason: err?.message }
      return usageMetricsRef.current
    }
  }

  return { status: 'not-enabled' }
}

// It would be better to convert this to a provider
// however, it would require to change other global variables
// such as cache and apolloClient to factory functions and use those
// functions in React components.
// TODO: Review this component together with https://github.com/sketch-hq/Cloud/issues/8812
export const UsageMetricsImport: React.FC = ({ children }) => {
  const [metricsState, setMetricsState] = useState(usageMetricsRef.current)
  useEffect(() => {
    loadUsageMetrics().then(x => {
      setMetricsState(x)
    })
  }, [])

  if (
    process.env.REACT_APP_ENV === 'dev' ||
    process.env.REACT_APP_ENV === 'test'
  ) {
    if (metricsState.status === 'non-init') {
      return null
    }
    return <>{children}</>
  }

  return <>{children}</>
}
